import React, { useState } from 'react';
import { backfillEarningsHistory } from '../../services/adminApi';
import { Modal, Button } from '../ui';

interface BackfillEarningsHistoryModalProps {
  onClose: () => void;
  onCompleted: () => void;
}

type Mode = 'full' | 'topOff';

const BackfillEarningsHistoryModal: React.FC<BackfillEarningsHistoryModalProps> = ({ onClose, onCompleted }) => {
  const [mode, setMode] = useState<Mode>('full');
  const [monthsBack, setMonthsBack] = useState<number>(3);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ written: number; skipped: number; errors: number } | null>(null);

  const handleRun = async () => {
    setError(null);
    setSummary(null);
    setLog([]);
    setRunning(true);
    try {
      await backfillEarningsHistory(
        { mode, monthsBack, dryRun },
        (ev) => {
          if (ev.type === 'started') {
            setLog(p => [...p, `Started (mode=${ev.mode}, dryRun=${ev.dryRun})`]);
          } else if (ev.type === 'discovered') {
            setLog(p => [...p, ev.message]);
          } else if (ev.type === 'month') {
            setLog(p => [...p, `[${ev.monthKey}] ${ev.message}`]);
          } else if (ev.type === 'done') {
            setLog(p => [...p, ev.message]);
            setSummary({ written: ev.written ?? 0, skipped: ev.skipped ?? 0, errors: ev.errors ?? 0 });
          } else if (ev.type === 'complete') {
            setLog(p => [...p, `Complete. Written ${ev.result.written.length}, errors ${ev.result.errors.length}`]);
            if (!dryRun) onCompleted();
          } else if (ev.type === 'error') {
            setError(ev.error || 'Backfill failed');
          }
        },
      );
    } catch (err: any) {
      setError(err?.message || 'Network error during backfill');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Backfill Earnings History"
      width={560}
      ariaLabel="Backfill Earnings History"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={running}>Cancel</Button>
          <Button variant="primary" onClick={handleRun} disabled={running}>
            {running ? 'Running…' : dryRun ? 'Run dry run' : 'Run backfill'}
          </Button>
        </>
      }
    >
      <p style={styles.muted}>
        Reads historical monthly payouts from the two CONSOLIDATED Google Sheets
        (Direct Sold + AdSense) and stores them in Firestore so the Earnings tab
        and Income Deck generator can use them. Months that already have
        ChannelMeter-sourced data are skipped (CM data is authoritative).
      </p>

      <div style={styles.section}>
        <div style={styles.label}>Mode</div>
        <label style={styles.radioRow}>
          <input type="radio" checked={mode === 'full'} onChange={() => setMode('full')} disabled={running} />
          Full — read every month from both sheets
        </label>
        <label style={styles.radioRow}>
          <input type="radio" checked={mode === 'topOff'} onChange={() => setMode('topOff')} disabled={running} />
          Top-off — only re-read the last&nbsp;
          <input
            type="number"
            min={1}
            max={24}
            value={monthsBack}
            onChange={(e) => setMonthsBack(Math.max(1, parseInt(e.target.value, 10) || 1))}
            style={styles.monthsInput}
            disabled={running || mode !== 'topOff'}
          />
          &nbsp;months
        </label>
      </div>

      <div style={styles.section}>
        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={running} />
          Dry run (no writes to Firestore)
        </label>
      </div>

      {log.length > 0 && (
        <div style={styles.progressBox}>
          {log.map((line, i) => (
            <div key={i} style={styles.progressLine}>{line}</div>
          ))}
        </div>
      )}
      {error && <div style={styles.error}>{error}</div>}
      {summary && (
        <div style={styles.resultBox}>
          Written: {summary.written}, Skipped: {summary.skipped}, Errors: {summary.errors}
          {dryRun && <div style={styles.muted}>Dry run — no Firestore writes.</div>}
        </div>
      )}
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 },
  label: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'var(--fg-4)',
  },
  muted: { fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.5, margin: 0 },
  radioRow: { display: 'flex', gap: 8, fontSize: 13, alignItems: 'center' },
  checkboxRow: { display: 'flex', gap: 8, fontSize: 13, alignItems: 'center' },
  monthsInput: {
    width: 60, padding: '4px 6px',
    border: '1px solid var(--border-2)', borderRadius: 'var(--radius-sm)', fontSize: 13,
  },
  progressBox: {
    background: 'var(--cg-neutral-25)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', padding: 12,
    maxHeight: 220, overflowY: 'auto',
    fontFamily: 'var(--font-mono, monospace)', fontSize: 11, lineHeight: 1.5,
    marginTop: 12,
  },
  progressLine: { color: 'var(--fg-1)' },
  error: {
    background: 'var(--danger-soft)', border: '1px solid var(--cg-terra-200)', color: 'var(--danger)',
    padding: '10px 12px', borderRadius: 'var(--radius-md)', fontSize: 13, marginTop: 12,
  },
  resultBox: {
    background: 'var(--success-soft)', border: '1px solid var(--cg-forest-200)', color: 'var(--success)',
    padding: 12, borderRadius: 'var(--radius-md)', fontSize: 13, marginTop: 12,
  },
};

export default BackfillEarningsHistoryModal;
